import { t } from 'i18next'
import { AuthUrl } from './url'
import { ToastType, UserRole } from './../../../types/generalType'
import { NavigateFunction } from 'react-router-dom'
/* eslint-disable camelcase */
import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit'
import { SsoHttpConfig as SSO_API, AppHttpConfig as APP_API } from '../../../services/httpConfig'
import {
  AuthForm,
  AuthFormType,
  ForgetPasswordForm,
  ForgetPasswordFormType,
  IAuthInitialState,
  RegisterUser,
  TChangePasswordData,
  UserAuth,
  UserAuthResponse,
} from './type'
import toastify from '../../../utils/toastify'
import decodeToken from '../../../utils/decodeToken'
import showResponseError from '../../../utils/showResponseError'

export const userInfoInitValue: UserAuth = {
  userId: '',
  accessToken: '',
  exp: 0,
  role: [],
  provinceBrokerId: '',
  name: '',
  family: '',
  nationalCode: '',
  phoneNumber: '',
}
const initialState: IAuthInitialState = {
  loading: false,
  userInfo: userInfoInitValue,
  authForm: 'login',
  forgetPasswordForm: 'login',
  changePassword: {
    loading: false,
  },
  createOtp: {
    createOtpLoading: false,
    createOtpStatus: false,
  },
  resetPasswordWithOtp: {
    resetOtpLoading: false,
    resetOtpStatus: false,
  },
}

// login user
export const loginUser = createAsyncThunk<
  UserAuthResponse,
  { loginData: string; navigate: NavigateFunction; setUser: any }
>('auth/loginUser', async (data) => {
  const { loginData, navigate, setUser } = data
  try {
    const response = await SSO_API.post('token', loginData)
      .then((res) => {
        if (res.status === 200) {
          const { role, accessToken } = decodeToken(res.data.access_token)
          const user = {
            role,
            accessToken,
          }
          setUser(user)
          if (user.role.includes(UserRole.PILGRIM)) {
            navigate('/PreReserve/ReserveNewPreReserve')
          } else {
            navigate('/')
          }
          return res.data
        }
      })
      .catch((error) => {
        showResponseError(error, 'TOP')
      })
    return response
  } catch (error) {
    showResponseError(error)
  }
})

// register user
export const registerUser = createAsyncThunk<RegisterUser, any>(
  'auth/registerUser',
  async (data, { dispatch }) => {
    try {
      const response = await APP_API.put(AuthUrl.RegisterUser, data)
        .then((res) => {
          if (res.status === 201) {
            toastify(ToastType.TopCenter, { operatin: t('register'), pageName: t('user') })
            dispatch(setAuthForm(AuthForm.LOGIN))
            return res.data
          }
        })
        .catch((error) => {
          showResponseError(error)
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)

// change password
export const changePassword = createAsyncThunk<
  any,
  { data: TChangePasswordData; navigate: NavigateFunction }
>('auth/ changePassword', async (command) => {
  const { data, navigate } = command
  try {
    const response = await APP_API.put(AuthUrl.ChangePassword, data)
      .then((res) => {
        if (res.status === 201) {
          toastify(ToastType.SUCCESS, { operatin: t('changePassword') })
          navigate('/')
          return res.data
        }
      })
      .catch((error) => {
        toastify(ToastType.SUCCESS, { operatin: t('changePassword') })
        showResponseError(error)
      })
    return response
  } catch (error) {
    showResponseError(error)
  }
})

// create otp
export const createOtp = createAsyncThunk<any, { nationalCode: string; mobile: string }>(
  'auth/createOtp',
  async (data) => {
    try {
      const response = await APP_API.post(AuthUrl.CreateOtp, { ...data, otpType: 1 })
        .then((res) => {
          if (res.status === 201) {
            return res.data
          }
        })
        .catch((error) => {
          toastify(ToastType.ERROR, { toastMessage: t('nationalCodeORMobileIsUnvalid') })
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)

// Reset Password With Otp
export const resetPasswordWithOtp = createAsyncThunk<
  any,
  { nationaCode: string; mobile: string; otpCode: string }
>('auth/resetPasswordWithOtp', async (data, { dispatch }) => {
  try {
    const response = await APP_API.put(AuthUrl.ResetPassword, data)
      .then((res) => {
        if (res.status === 201) {
          toastify(ToastType.TopCenter, { toastMessage: t('sendNewPassword') })
          dispatch(setForgetPasswordForm(ForgetPasswordForm.LOGIN))
          return res.data
        }
      })
      .catch((error) => {
        toastify(ToastType.ERROR, { toastMessage: t('otpIsInvalid') })
      })
    return response
  } catch (error) {
    showResponseError(error)
  }
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.userInfo = userInfoInitValue
    },
    // set user info
    setUserInfo: (state, action: PayloadAction<UserAuth>) => {
      state.userInfo = action.payload
    },
    // set auth form for show
    setAuthForm: (state, action: PayloadAction<AuthForm>) => {
      state.authForm = action.payload
    },
    setForgetPasswordForm: (state, action: PayloadAction<ForgetPasswordForm>) => {
      state.forgetPasswordForm = action.payload
    },
    setCreateOtpStatus: (state, action: PayloadAction<boolean>) => {
      state.createOtp.createOtpStatus = action.payload
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IAuthInitialState>) => {
    // login user
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.userInfo = userInfoInitValue
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<UserAuthResponse, string>) => {
        state.loading = false
      })
      .addCase(loginUser.rejected, (state) => {
        state.loading = false
        state.userInfo = userInfoInitValue
      })
    // register user
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false
        // state.authForm = AuthForm.LOGIN
      })
      .addCase(registerUser.rejected, (state) => {
        state.loading = false
      })
    // Change password
    builder
      .addCase(changePassword.pending, (state) => {
        state.changePassword.loading = true
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.changePassword.loading = false
      })
      .addCase(changePassword.rejected, (state) => {
        state.changePassword.loading = false
      })
    // Create Otp
    builder
      .addCase(createOtp.pending, (state) => {
        state.createOtp.createOtpLoading = true
      })
      .addCase(createOtp.fulfilled, (state, action: PayloadAction<boolean>) => {
        state.createOtp.createOtpLoading = false
        state.createOtp.createOtpStatus = action.payload
      })
      .addCase(createOtp.rejected, (state) => {
        state.createOtp.createOtpLoading = false
      })
    // Reset Password
    builder
      .addCase(resetPasswordWithOtp.pending, (state) => {
        state.resetPasswordWithOtp.resetOtpLoading = true
      })
      .addCase(resetPasswordWithOtp.fulfilled, (state) => {
        state.resetPasswordWithOtp.resetOtpLoading = false
        state.resetPasswordWithOtp.resetOtpStatus = true
      })
      .addCase(resetPasswordWithOtp.rejected, (state) => {
        state.resetPasswordWithOtp.resetOtpLoading = false
      })
  },
})

export const { logoutUser, setUserInfo, setAuthForm, setForgetPasswordForm, setCreateOtpStatus } =
  authSlice.actions

export default authSlice.reducer
